import type {
    OrderingPropertyMeta,
    ValueHintBoolean,
    ValueHintEnumSamples,
    ValueHintNumericRange,
} from "./orderingPropertyTypes"

export function isEnumHint(h: OrderingPropertyMeta["value_hint"]): h is ValueHintEnumSamples {
    return h?.kind === "enum_samples"
}

function isRangeHint(h: OrderingPropertyMeta["value_hint"]): h is ValueHintNumericRange {
    return h?.kind === "numeric_range"
}

function isBooleanHint(h: OrderingPropertyMeta["value_hint"]): h is ValueHintBoolean {
    return h?.kind === "boolean"
}

export type RankingPromptInsertMode = "property" | "sample" | "direction" | "filter"

export type NumericFilterOp = "gt" | "lt"

export type RankingPromptInsertOptions = {
    mode?: RankingPromptInsertMode
    /** For enum_samples — specific sample value */
    sample?: string
    /** For numeric ordering */
    direction?: "higher" | "lower"
    /** For numeric threshold filters */
    filterOp?: NumericFilterOp
    filterValue?: string | number
    /** For boolean filters */
    booleanValue?: boolean
}

function formatRange(min: number, max: number): string {
    const fmt = (n: number) =>
        Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2)))
    return `${fmt(min)} a ${fmt(max)}`
}

/**
 * Builds a natural-language ranking criterion snippet for the chat input.
 */
export function buildRankingPromptSnippet(
    property: OrderingPropertyMeta,
    options: RankingPromptInsertOptions = {}
): string {
    const { name } = property
    const mode = options.mode ?? "property"
    const hint = property.value_hint

    if (mode === "sample" && options.sample) {
        return `priorizar produtos com ${name} igual a ${options.sample}`
    }

    if (property.type === "boolean" || isBooleanHint(hint)) {
        const val = options.booleanValue ?? true
        return val
            ? `incluir apenas produtos com ${name} ativo`
            : `incluir apenas produtos com ${name} inativo`
    }

    if (mode === "filter" && options.filterOp) {
        const val = String(options.filterValue ?? "").trim()
        if (!val) {
            return `filtrar produtos por ${name} (${options.filterOp === "gt" ? "maior que" : "menor que"} um valor)`
        }
        const opPhrase = options.filterOp === "gt" ? "maior que" : "menor que"
        if (isRangeHint(hint)) {
            return `filtrar apenas produtos com ${name} ${opPhrase} ${val} (faixa observada: ${formatRange(hint.min, hint.max)})`
        }
        return `filtrar apenas produtos com ${name} ${opPhrase} ${val}`
    }

    if (mode === "direction") {
        const dir = options.direction ?? "higher"
        const dirWord = dir === "higher" ? "maior" : "menor"
        if (isRangeHint(hint)) {
            return `ordenar por ${dirWord} ${name} (faixa observada: ${formatRange(hint.min, hint.max)})`
        }
        return `ordenar por ${dirWord} ${name}`
    }

    if (property.type === "integer" || property.type === "decimal") {
        const dirWord = "maior"
        if (isRangeHint(hint)) {
            return `ordenar por ${dirWord} ${name} (faixa observada: ${formatRange(hint.min, hint.max)})`
        }
        return `ordenar por ${dirWord} ${name}`
    }

    if (isEnumHint(hint) && hint.samples.length > 0) {
        const samples = hint.samples.slice(0, 3).join(", ")
        return `ordenar ou filtrar por ${name} (ex.: ${samples})`
    }

    if (isRangeHint(hint)) {
        return `ordenar por ${name} (valores entre ${formatRange(hint.min, hint.max)})`
    }

    return `ordenar produtos por ${name}`
}

export function formatPropertyTypeLabel(type: OrderingPropertyMeta["type"]): string {
    switch (type) {
        case "string":
            return "texto"
        case "integer":
            return "inteiro"
        case "decimal":
            return "decimal"
        case "boolean":
            return "sim/não"
        default:
            return "—"
    }
}

export function formatValueHintSummary(property: OrderingPropertyMeta): string | null {
    const hint = property.value_hint
    if (!hint) return null
    if (isEnumHint(hint) && hint.samples.length) {
        return hint.samples.join(" · ")
    }
    if (isRangeHint(hint)) {
        return formatRange(hint.min, hint.max)
    }
    if (isBooleanHint(hint)) {
        return hint.values.map((v: boolean) => (v ? "sim" : "não")).join(" · ")
    }
    return null
}
